import { ChevronUp, Loader2, Plus } from "lucide-react"
import { type ReactNode, useState } from "react"
import { JobElapsedTimer, jobKindLabel, statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { Button } from "@/components/ui/primitives"
import { useProgressClock } from "@/hooks/use-progress-clock"
import { useTranslation } from "@/hooks/use-translation"
import { estimatedJobProgress, isEstimatedGenerationPhase } from "@/lib/job-estimated-progress"
import type { GenerationJobRow } from "@/types/workspace"

/** Плашка без GenerationJob: заголовок, подэтап jobs.statusDetail, таймер. Процент не задаём. */
export type ShotGenerationStatusView = {
  title: string
  statusDetail?: string | null
  startedAt?: string | null
}

export function ShotGenerationStatusRow({
  job,
  status,
  title,
  expandable = true,
  children,
}: {
  job?: GenerationJobRow | null
  status?: ShotGenerationStatusView | null
  title?: string
  /** false — плашка без плюса и без слота действий. Генерации оставляют плюс. */
  expandable?: boolean
  children?: ReactNode
}) {
  const { t } = useTranslation("jobs")
  const [expanded, setExpanded] = useState(false)
  const detailKey = status?.statusDetail ?? job?.status_detail
  const detail = detailKey ? statusDetailLabel(detailKey) : null
  const timerStart = status?.startedAt ?? job?.started_at ?? job?.created_at
  const now = useProgressClock(job ? isEstimatedGenerationPhase(job) : false)
  const progress = job ? estimatedJobProgress(job, now) : 0
  const heading = title || status?.title || job?.subject_label || (job ? jobKindLabel(job.job_kind) : "")

  return (
    <div className="flex w-full flex-col items-center gap-2">
      <div className="flex w-full max-w-xl items-center gap-2 rounded-lg border border-ui-accent bg-ui-accent/10 px-3 py-2">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ui-accent-text" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium dark:text-violet-100 light:text-ui-accent-text">{heading}</div>
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-ui-accent-text/80">
            {detail ? <span>{detail}</span> : null}
            {timerStart ? <JobElapsedTimer startedAt={timerStart} active /> : null}
            {progress > 0 ? <span>{progress}%</span> : null}
          </div>
        </div>
        {expandable ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0 text-ui-accent-text hover:dark:text-violet-100 light:text-ui-accent-text"
            title={expanded ? t("hideActions") : t("startParallelGeneration")}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          </Button>
        ) : null}
      </div>
      {expandable && expanded ? (
        <div className="flex flex-wrap items-start justify-center gap-2">{children}</div>
      ) : null}
    </div>
  )
}

/** Обёртка: при активной задаче — статус-строка, иначе — обычный ряд кнопок. */
export function ShotGenerationActions({
  activeJob,
  status,
  title,
  expandable = true,
  children,
}: {
  activeJob?: GenerationJobRow | null
  status?: ShotGenerationStatusView | null
  title?: string
  /** false — плашка без плюса. Имеет смысл вместе со status, не с activeJob. */
  expandable?: boolean
  children: ReactNode
}) {
  if (activeJob) {
    return (
      <ShotGenerationStatusRow job={activeJob} title={title}>
        {children}
      </ShotGenerationStatusRow>
    )
  }
  if (status) {
    return (
      <ShotGenerationStatusRow status={status} title={title} expandable={expandable}>
        {children}
      </ShotGenerationStatusRow>
    )
  }
  // items-start: кнопки с подписью модели и без неё выравниваются по верхнему краю.
  // w-full: замер collapseToIcon видит ширину колонки, а не ширину текста.
  return <div className="flex w-full min-w-0 flex-wrap items-start justify-center gap-2">{children}</div>
}
